import type { ResourceAccessEntry } from '@cotera/client/app/etc/api/typed-client';
import type { FolderScope } from '../resources/list/resource-list-types';
import type { WorkflowSummary } from '../workflow/use-workflow-summaries';
import {
  isVirtualChatsItem,
  type FolderContentsListItem,
} from './folder-contents-list-items';
import { getFolderContentItemFolderScope } from './folder-contents-item-display';
import {
  getLibraryItemSummary,
  type LibraryItemSummary,
} from './library-item-summary';

/** Everything a Library row or card needs to render, already derived. */
export type LibraryItemDisplay = {
  name: string;
  /** One-line secondary signal, or null when the item has nothing to say. */
  summary: LibraryItemSummary | null;
  /** Effective scope, for the scope badge. Null for virtual rows. */
  scope: FolderScope | null;
  /** Owner + explicit grantees, for the "shared with" avatar stack. */
  users: ResourceAccessEntry[] | undefined;
  ownerId: string | undefined;
  /**
   * Who made the item, for the "Created by" column. These payloads carry no
   * distinct `createdBy` — `ownerId` is the only person reference on them, and
   * nothing in the product transfers ownership, so it stands in. Chats name
   * their actor instead. Undefined for virtual rows, which nobody created.
   */
  creatorId: string | undefined;
  /** Someone else owns it → show the owner rather than the scope. */
  isInboundShare: boolean;
  dateLabel: string;
  hasUnreadAssistant: boolean;
};

/** Compact list date: today's time ("1:01 PM"), else "Jul 13" (year added for other years). */
export function formatListItemDate(date: Date): string {
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  if (sameDay) {
    return date.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
  }
  const includeYear = date.getFullYear() !== now.getFullYear();
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(includeYear ? { year: 'numeric' as const } : {}),
  });
}

/**
 * Derives display values for a Library item.
 *
 * Rows and cards show the same facts in different shapes, so this is the one
 * place the union payload is unpacked. Keeping it here means the two components
 * stay presentational and can't disagree about, say, which folder an item is
 * filed in — and the handful of unavoidable narrowing casts live in a single
 * file instead of being repeated in both renderers.
 */
export function getLibraryItemDisplay(params: {
  item: FolderContentsListItem;
  /** Current user id — an item owned by anyone else is an inbound share. */
  userId: string;
  /** Scope of the folder being viewed, for items that inherit it. */
  nestedFolderScope: FolderScope | null;
  /** Top level flattens across folders, so items show where they're filed. */
  isTopLevel: boolean;
  workflowSummaries: Map<string, WorkflowSummary>;
  /** Which timestamp to show, matching the active sort. */
  dateField: 'updatedAt' | 'createdAt';
  /** Grid cards opt in; a list row has no space for a description. */
  includeDescription?: boolean;
}): LibraryItemDisplay {
  const {
    item,
    userId,
    nestedFolderScope,
    isTopLevel,
    workflowSummaries,
    dateField,
    includeDescription = false,
  } = params;

  const isVirtual = isVirtualChatsItem(item);

  const name = isVirtual
    ? item.data.title
    : ((): string => {
        const data: { name?: string; title?: string | null } = item.data;
        return data.name ?? data.title ?? 'Untitled';
      })();

  // Which folder this item lives in. Prefer the full path ("Marketing / Q3");
  // fall back to the immediate folder name. Only meaningful at the top level —
  // inside a folder it would just repeat the folder you're already in.
  const folderLabel = ((): string | null => {
    if (isVirtual || !isTopLevel) {
      return null;
    }
    // Asserted rather than annotated: only some members of the item union carry
    // folder placement, so a structural annotation would not accept the whole
    // union. Narrow object assertions (never `unknown`/`any`) are the pattern
    // used throughout these list payloads.
    const data = item.data as {
      folderPath?: string[] | null;
      folderName?: string | null;
    };
    const path = data.folderPath ?? null;
    const label =
      path !== null && path.length > 0
        ? path.join(' / ')
        : data.folderName ?? null;
    return label !== null && label !== '' ? label : null;
  })();

  const dateLabel = ((): string => {
    const data: { createdAt?: string | Date; updatedAt?: string | Date } =
      item.data;
    const raw = data[dateField];
    return raw === undefined || raw === null
      ? ''
      : formatListItemDate(new Date(raw));
  })();

  const access: { users?: ResourceAccessEntry[]; ownerId?: string } = isVirtual
    ? {}
    : item.data;
  const ownerId = access.ownerId;

  const creatorId = ((): string | undefined => {
    if (isVirtual) {
      return undefined;
    }
    // A chat has no owner column of its own; its actor is who created it.
    return item.type === 'chat' ? item.data.actorId : ownerId;
  })();

  return {
    name,
    summary: getLibraryItemSummary({
      item,
      workflowSummaries,
      folderLabel,
      includeDescription,
    }),
    scope: isVirtual
      ? null
      : getFolderContentItemFolderScope(item, nestedFolderScope),
    users: access.users,
    ownerId,
    creatorId,
    isInboundShare: ownerId !== undefined && ownerId !== userId,
    dateLabel,
    hasUnreadAssistant:
      item.type === 'chat' && item.data.hasUnreadAssistant === true,
  };
}
